import { startCase } from 'lodash-es'
import { Link } from 'react-router'
import {
  type AgentDocument,
  type AgentRunSummary,
  formatTimestamp,
  REVIEWED_STATUSES,
} from '~/entities/document-agent'
import { cn } from '~/shared/lib/cn'
import { pluralize } from '~/shared/lib/pluralize'
import { IconButton, LocalIcon, Text } from '~/shared/ui'
import { RunSelector } from './run-selector'

const LINK_CLASS_NAME =
  'text-text-link flex items-center gap-1 text-[14px] whitespace-nowrap no-underline hover:underline'

export function AgentRunHeader({
  document,
  runs,
  selectedRunId,
  onSelectRun,
}: {
  document: AgentDocument
  runs: AgentRunSummary[]
  selectedRunId: string | undefined
  onSelectRun: (runId: string) => void
}) {
  const documentPath = `/documents/${encodeURIComponent(document.short_key)}`
  const details = [
    document.source,
    document.inbox_name,
    typeof document.page_count === 'number'
      ? `${document.page_count} ${pluralize(document.page_count, 'page')}`
      : null,
    document.document_date ? formatTimestamp(document.document_date) : null,
  ].filter(Boolean)
  const isReviewed = REVIEWED_STATUSES.has(document.review_status ?? '')

  return (
    <div className="border-divider flex shrink-0 flex-wrap items-center justify-between gap-4 border-b py-3 pr-6 pl-2">
      <div className="flex min-w-0 items-center gap-2">
        <IconButton
          to="/agent-runs"
          size="medium"
          container="medium"
          materialIcon="arrow_back"
        />
        <div className="flex min-w-0 flex-col">
          <div className="flex items-center gap-2">
            <Text
              appearance="H6"
              copyable
            >
              {document.short_key}
            </Text>
            {document.review_status && (
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-[12px] leading-4 font-medium whitespace-nowrap',
                  isReviewed
                    ? 'bg-alert-success-fill text-success-content'
                    : 'bg-surface-medium text-text-primary',
                )}
              >
                {startCase(document.review_status)}
              </span>
            )}
          </div>
          <Text
            className="text-text-secondary"
            appearance="CAPTION"
          >
            {details.join(' · ')}
          </Text>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-5">
        {runs.length > 0 && (
          <RunSelector
            runs={runs}
            value={selectedRunId}
            onChange={onSelectRun}
          />
        )}
        <Link
          className={LINK_CLASS_NAME}
          to={documentPath}
          target="_blank"
        >
          <LocalIcon
            name="openInNew"
            size="18"
          />
          Open in Opti
        </Link>
      </div>
    </div>
  )
}
